/**
 * Opening a Telegram chat in the app rather than on t.me.
 *
 * `tg://resolve` hands the chat straight to the installed client. That is the
 * whole point of it: a `t.me` link answers a browser with an interstitial page
 * whose only content is a button doing what the link already said, and a driver
 * standing at a charger has to tap through it before anything happens.
 *
 * The scheme fails silently when no client is registered for it - no error, no
 * navigation, a dead tap - so the web address follows on a timer. The timer is
 * cancelled as soon as the page loses focus or is hidden, because a client
 * taking the foreground is exactly what that looks like; the fallback therefore
 * only ever fires when nothing happened.
 */

/** Long enough for a cold app launch to blur the page, short enough to feel like the tap did it. */
const FALLBACK_MS = 1200

export const telegramAppUrl = (username: string): string => `tg://resolve?domain=${username}`

export const telegramWebUrl = (username: string): string => `https://t.me/${username}`

/**
 * Arm the web fallback for a link that has already been given `tg://` to follow.
 *
 * The navigation itself stays on the anchor's `href`, so a client is reached by
 * the browser rather than by us, and the link still copies, middle-clicks and
 * reads as a link. This only watches whether anything came of it.
 */
export const scheduleTelegramFallback = (username: string): void => {
  const timer = window.setTimeout(() => {
    if (document.visibilityState === 'visible' && document.hasFocus())
      window.location.assign(telegramWebUrl(username))
  }, FALLBACK_MS)

  const cancel = (): void => {
    window.clearTimeout(timer)
    window.removeEventListener('blur', cancel)
    window.removeEventListener('pagehide', cancel)
    document.removeEventListener('visibilitychange', onHide)
  }

  const onHide = (): void => {
    if (document.visibilityState === 'hidden') cancel()
  }

  window.addEventListener('blur', cancel)
  window.addEventListener('pagehide', cancel)
  document.addEventListener('visibilitychange', onHide)
}
